// client/src/pages/application/SettingsPage.js
// Settings — Account · Subscription · Team — consistent with Dashboard/Marketing

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TeamMembers from '../../components/settings/TeamMembers';
import BusinessProfileSettings from '../../components/settings/BusinessProfileSettings';
import apiClient from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ─── helpers ─────────────────────────────────────────────────────── */
const getToken = () => localStorage.getItem('token');
const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw body;
  return body;
};

function calcStrength(p) {
  let s = 0;
  if (p.length >= 8)  s++;
  if (p.match(/[a-z]/) && p.match(/[A-Z]/)) s++;
  if (p.match(/\d/))  s++;
  if (p.match(/[^a-zA-Z\d]/)) s++;
  return s;
}

/* ─── PRO FEATURES LIST ───────────────────────────────────────────── */
const FREE_FEATURES = [
  '14-day full access trial',
  'AI-generated 30-day content plan',
  'Design Studio (basic)',
  'Facebook & Instagram connection',
  'Post scheduling & publishing',
  'Campaign calendar',
  'Results & analytics',
];
const PRO_FEATURES = [
  'Everything in Free, forever',
  'Unlimited AI content generation',
  'Full Design Studio + video editor',
  'All platform connections (LinkedIn, TikTok)',
  'Team members (unlimited)',
  'Priority AI campaign analysis',
  'Strategy auto-updates from results',
  'PDF campaign reports',
  'Priority support',
];

/* ─── sub-components ──────────────────────────────────────────────── */
function SectionCard({ title, icon, children }) {
  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
        <i className={`${icon} text-slate-400 text-sm`}></i>
        <h2 className="text-[13px] font-bold text-slate-800 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function PasswordField({ id, label, value, show, onToggle, onChange, placeholder, error }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[12px] font-semibold text-slate-600 mb-1.5">{label}</label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <i className="fas fa-lock text-slate-400 text-sm"></i>
        </div>
        <input
          id={id} type={show ? 'text' : 'password'} value={value}
          onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className={`w-full pl-9 pr-10 py-2.5 border rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-300' : 'border-gray-200'}`}
        />
        <button type="button" onClick={onToggle}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600">
          <i className={`fas ${show ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
        </button>
      </div>
      {error && <p className="mt-1 text-[11px] text-red-600"><i className="fas fa-exclamation-circle mr-1"></i>{error}</p>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ACCOUNT TAB
══════════════════════════════════════════════════════════════════════ */
function AccountTab({ email }) {
  const [form, setForm]         = useState({ current: '', newPwd: '', confirm: '' });
  const [show, setShow]         = useState({ current: false, newPwd: false, confirm: false });
  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState('');
  const strength                = calcStrength(form.newPwd);
  const strengthColour          = ['bg-red-500','bg-red-500','bg-yellow-500','bg-blue-500','bg-green-500'][strength];
  const strengthText            = ['Weak','Weak','Fair','Good','Strong'][strength];
  const strengthTextColour      = ['text-red-600','text-red-600','text-yellow-600','text-blue-600','text-green-600'][strength];

  const toggle = k => setShow(p => ({ ...p, [k]: !p[k] }));
  const setF   = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); setSuccess(''); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.current)                  errs.current = 'Current password required';
    if (!form.newPwd)                   errs.newPwd  = 'New password required';
    else if (form.newPwd.length < 6)    errs.newPwd  = 'At least 6 characters';
    if (form.newPwd !== form.confirm)   errs.confirm = 'Passwords do not match';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      await apiClient.post('/auth/change-password', {
        currentPassword: form.current,
        newPassword:     form.newPwd,
        confirmPassword: form.confirm,
      });
      setSuccess('Password changed successfully!');
      setForm({ current: '', newPwd: '', confirm: '' });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.error || 'Failed to change password.';
      setErrors({ current: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Email info */}
      <SectionCard title="Email Address" icon="fas fa-envelope">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
            <i className="fas fa-user text-blue-600 text-sm"></i>
          </div>
          <div>
            <p className="text-[13px] font-semibold text-slate-800">{email || 'Not available'}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Your account email address — cannot be changed</p>
          </div>
          <span className="ml-auto text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">Verified</span>
        </div>
      </SectionCard>

      {/* Change password */}
      <SectionCard title="Change Password" icon="fas fa-lock">
        <form onSubmit={submit} className="space-y-4 max-w-md">
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <i className="fas fa-check-circle text-green-600 text-sm"></i>
              <p className="text-[12px] text-green-700 font-medium">{success}</p>
            </div>
          )}

          <PasswordField id="current" label="Current Password" value={form.current}
            show={show.current} onToggle={() => toggle('current')}
            onChange={v => setF('current', v)} placeholder="Enter current password"
            error={errors.current} />

          <PasswordField id="newPwd" label="New Password" value={form.newPwd}
            show={show.newPwd} onToggle={() => toggle('newPwd')}
            onChange={v => setF('newPwd', v)} placeholder="Create a new password"
            error={errors.newPwd} />

          {form.newPwd && (
            <div className="flex items-center gap-2 -mt-2">
              <div className="flex-1 bg-gray-200 h-1.5 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-300 ${strengthColour}`}
                  style={{ width: `${(strength / 4) * 100}%` }} />
              </div>
              <span className={`text-[11px] font-semibold ${strengthTextColour}`}>{strengthText}</span>
            </div>
          )}

          <PasswordField id="confirm" label="Confirm New Password" value={form.confirm}
            show={show.confirm} onToggle={() => toggle('confirm')}
            onChange={v => setF('confirm', v)} placeholder="Repeat new password"
            error={errors.confirm} />

          <button type="submit" disabled={saving}
            className="bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white text-[12px] font-semibold px-5 py-2.5 rounded-lg transition-colors">
            {saving ? <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin inline-block mr-2"></div>Saving…</> : 'Update Password'}
          </button>
        </form>
      </SectionCard>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   SUBSCRIPTION TAB
══════════════════════════════════════════════════════════════════════ */
function SubscriptionTab({ showSuccess, sessionId }) {
  const [sub, setSub]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [banner, setBanner]   = useState(null);

  // On mount: verify Stripe session if present, then load subscription
  useEffect(() => {
    const init = async () => {
      // If returning from Stripe with a session_id, verify it first
      if (sessionId) {
        try {
          await api(`/api/subscription/verify?session_id=${encodeURIComponent(sessionId)}`);
          setBanner({ type: 'success', text: "You're now on Digital Trent Pro! Welcome to the full experience." });
        } catch (err) {
          // Already activated (e.g. webhook fired first) — not an error
          if (showSuccess) {
            setBanner({ type: 'success', text: "You're now on Digital Trent Pro! Welcome to the full experience." });
          }
        }
      } else if (showSuccess) {
        setBanner({ type: 'success', text: "You're now on Digital Trent Pro! Welcome to the full experience." });
      }

      // Load current subscription
      try {
        const d = await api('/api/subscription');
        setSub(d.data);
      } catch {
        setSub(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const result = await api('/api/subscription/checkout', {
        method: 'POST',
        body: JSON.stringify({ tier: 'pro' }),
      });
      if (result.data?.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
      }
    } catch (e) {
      alert(e?.message || e?.error || 'Failed to start checkout. Please try again.');
      setUpgrading(false);
    }
  };

  const isPro          = sub?.currentTier === 'pro' && sub?.status === 'active';
  const isTrialActive  = sub?.status === 'trial';
  const trialDays      = sub?.trialDaysRemaining ?? 0;
  const trialExpired   = !isPro && !isTrialActive;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {banner && (
        <div className={`flex items-center gap-3 p-3.5 rounded-xl border text-sm ${banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          <i className={`fas fa-check-circle ${banner.type === 'success' ? 'text-green-600' : 'text-red-600'}`}></i>
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)} className="ml-auto text-slate-400 hover:text-slate-600"><i className="fas fa-times"></i></button>
        </div>
      )}

      {/* Current status */}
      <div className={`bg-white border-2 rounded-xl p-5 ${isPro ? 'border-blue-300' : isTrialActive ? 'border-amber-300' : 'border-red-300'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPro ? 'bg-blue-600' : isTrialActive ? 'bg-amber-500' : 'bg-red-500'}`}>
              <i className={`fas ${isPro ? 'fa-crown' : isTrialActive ? 'fa-clock' : 'fa-exclamation'} text-white text-sm`}></i>
            </div>
            <div>
              <div className="text-[14px] font-bold text-slate-800">
                {isPro ? 'Digital Trent Pro' : isTrialActive ? 'Free Trial' : 'Trial Expired'}
              </div>
              <div className="text-[12px] text-slate-500">
                {isPro && `Active subscription · £39/month`}
                {isTrialActive && `${trialDays} day${trialDays !== 1 ? 's' : ''} remaining`}
                {trialExpired && 'Your trial has ended — upgrade to continue'}
              </div>
            </div>
          </div>
          {!isPro && (
            <button onClick={upgrade} disabled={upgrading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-[12px] font-bold px-5 py-2.5 rounded-xl transition-colors shrink-0">
              {upgrading
                ? <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Redirecting…</>
                : <><i className="fas fa-crown"></i>Upgrade to Pro — £39/month</>}
            </button>
          )}
        </div>
      </div>

      {/* Tier comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Free */}
        <div className={`bg-white border-2 rounded-xl p-5 ${!isPro ? 'border-blue-200' : 'border-gray-200'}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Free Trial</div>
              <div className="text-[28px] font-extrabold text-slate-800 leading-none">£0</div>
              <div className="text-[12px] text-slate-400 mt-0.5">14 days</div>
            </div>
            {!isPro && (
              <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">Current Plan</span>
            )}
          </div>
          <div className="space-y-2">
            {FREE_FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-2">
                <i className="fas fa-check text-green-500 text-xs mt-0.5 shrink-0"></i>
                <span className="text-[12px] text-slate-600">{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pro */}
        <div className={`bg-white border-2 rounded-xl p-5 relative overflow-hidden ${isPro ? 'border-blue-500' : 'border-gray-200'}`}>
          {isPro && (
            <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
              ACTIVE
            </div>
          )}
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-1">Pro Plan</div>
              <div className="text-[28px] font-extrabold text-slate-800 leading-none">£39</div>
              <div className="text-[12px] text-slate-400 mt-0.5">per month</div>
            </div>
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <i className="fas fa-crown text-white text-sm"></i>
            </div>
          </div>
          <div className="space-y-2 mb-5">
            {PRO_FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-2">
                <i className="fas fa-check text-blue-500 text-xs mt-0.5 shrink-0"></i>
                <span className="text-[12px] text-slate-600">{f}</span>
              </div>
            ))}
          </div>
          {!isPro && (
            <button onClick={upgrade} disabled={upgrading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-[12px] font-bold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2">
              {upgrading
                ? <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Redirecting to Stripe…</>
                : <><i className="fas fa-crown"></i>Get Pro — £39/month</>}
            </button>
          )}
        </div>
      </div>

      {/* Stripe badge */}
      <div className="flex items-center justify-center gap-2 py-2">
        <i className="fas fa-lock text-slate-400 text-xs"></i>
        <span className="text-[11px] text-slate-400">Secure payment via Stripe · Cancel anytime · No hidden fees</span>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   MAIN SETTINGS PAGE
══════════════════════════════════════════════════════════════════════ */
export default function SettingsPage() {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('account');
  const [email, setEmail]         = useState('');
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading]     = useState(true);

  // Handle subscription success redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('subscription') === 'success') setActiveTab('subscription');
  }, [location.search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Get email from localStorage (set on login)
      const storedEmail = localStorage.getItem('userEmail') || '';
      setEmail(storedEmail);

      // Also try to get from /api/auth/me
      if (!storedEmail) {
        try {
          const me = await api('/api/auth/me');
          if (me.user?.email) { setEmail(me.user.email); localStorage.setItem('userEmail', me.user.email); }
        } catch { /* silent */ }
      }

      // Load business data for team tab
      const { businessAPI } = await import('../../services/businessAPI');
      const res = await businessAPI.getProfile().catch(() => null);
      if (res?.success) setBusinessData(res.profile);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const TABS = [
    { id: 'account',      label: 'Account',          icon: 'fas fa-user-cog'   },
    { id: 'subscription', label: 'Subscription',      icon: 'fas fa-credit-card' },
    { id: 'business',     label: 'Business Profile',  icon: 'fas fa-building'   },
    { id: 'team',         label: 'Team Members',      icon: 'fas fa-users'      },
  ];

  const handleBusinessUpdate = (profile) => setBusinessData(profile);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="p-5 space-y-4 flex-1">

          {/* ── Header ─────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-extrabold text-slate-800">Settings</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">Account, subscription and team management</p>
            </div>
          </div>

          {/* ── Tabs ───────────────────────────────────────────── */}
          <div className="flex items-center gap-0.5 border-b border-gray-200 overflow-x-auto">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}>
                <i className={`${tab.icon} text-[11px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Tab content ────────────────────────────────────── */}
          {activeTab === 'account'      && <AccountTab email={email} />}
          {activeTab === 'subscription' && (
            <SubscriptionTab
              showSuccess={new URLSearchParams(location.search).get('subscription') === 'success'}
              sessionId={new URLSearchParams(location.search).get('session_id')}
            />
          )}
          {activeTab === 'business'     && (
            <BusinessProfileSettings businessData={businessData} onUpdate={handleBusinessUpdate} />
          )}
          {activeTab === 'team'         && (
            <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
              <TeamMembers businessData={businessData} />
            </div>
          )}

        </div>
      </div>
    </DashboardLayout>
  );
}
